import { ChevronDown } from 'lucide-react';
import { GroupCompactRow } from '../../group-chat/GroupAvatars';
import Link from 'next/link';
import type { ConversationsState } from '../../../../lib/conversations';
import { useConversationActions, CONVERSATIONS_PAGE, type ConversationActionsFlow } from '../../../../lib/use-conversation-actions';
import { ConversationRowMenu } from '../../conversations/ConversationRowMenu';
import { DeleteConversationConfirm } from '../../conversations/DeleteConversationConfirm';
import { RenameConversationDialog } from '../../conversations/RenameConversationDialog';
import { PinIcon } from '../components/icons';
import { useRecentChats } from '../hooks/use-recent-chats';
import { RECENT_CHATS_DEFAULT_OPEN } from '../shared';
import styles from './MobileSidebar.module.css';
import rowStyles from '../components/ConversationRow.module.css';
import { SeeAllChatsLink } from '../components/SeeAllChatsLink';
import { WhatsAppGlyph } from '../../settings-whatsapp/WhatsAppGlyph';

// The mobile drawer's Pinned Chats group — the same threads the wide rail lists (useRecentChats), as the
// design's disclosure row with the chevron opposite. Open by default, same as the wide rail.
function MobileChatGroup({
  state,
  selectedId,
  flow,
}: {
  state: ConversationsState;
  selectedId: string | null;
  flow: ConversationActionsFlow;
}) {
  const { chats, open, toggle, label } = useRecentChats({ state, selectedId, defaultOpen: RECENT_CHATS_DEFAULT_OPEN, section: 'pinned' });
  if (chats.length === 0) return null;

  return (
    <section className={`${styles.group} ${styles.groupRecent}`}>
      <button type="button" onClick={toggle} aria-expanded={open} className={`${styles.row} ${styles.recentHeader}`}>
        <span aria-hidden className={styles.rowIcon}><PinIcon /></span>
        <span className={styles.rowLabel}>{label}</span>
        <ChevronDown aria-hidden size={24} className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} />
      </button>
      {open && (
        <div className={styles.chatList}>
          {chats.map((chat) => (
            <div key={chat.conv.id} className={rowStyles.row}>
              <Link
                href={chat.route}
                as={chat.path}
                shallow
                onClick={chat.onClick}
                aria-current={chat.isSelected ? 'page' : undefined}
                className={`${styles.chatRow} ${rowStyles.link}`}
              >
                {chat.group ? (
                  <GroupCompactRow summary={chat.group} title={chat.title} />
                ) : (
                  <>
                    {chat.waGroup ? (
                      <span style={{ display: 'flex', alignItems: 'center', minWidth: 0, maxWidth: '100%' }}>
                        <span aria-hidden style={{ display: 'inline-flex', flexShrink: 0, marginInlineEnd: 6 }}><WhatsAppGlyph size={14} /></span>
                        <span className={styles.chatTitle}>{chat.title}</span>
                      </span>
                    ) : (
                      <span className={styles.chatTitle}>{chat.title}</span>
                    )}
                    {chat.channelBadge && <span className={styles.chatChannel}>{chat.channelBadge}</span>}
                  </>
                )}
              </Link>
              <span className={rowStyles.menu}>
                <ConversationRowMenu conv={chat.conv} title={chat.title} flow={flow} />
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/// The drawer's conversation group — PINNED only; see the long note on the wide rail's
/// `ConversationsSection`, which this mirrors exactly.
///
/// The delete confirm is mounted ONCE here rather than inside the group's rows — two mounted dialogs sharing
/// one flow would both open on the same request.
export function MobileRecentChats({
  state,
  selectedId,
  onClose,
}: {
  state: ConversationsState;
  selectedId: string | null;
  onClose?: () => void;
}) {
  const flow = useConversationActions(CONVERSATIONS_PAGE);
  return (
    <>
      <MobileChatGroup state={state} selectedId={selectedId} flow={flow} />
      <SeeAllChatsLink onClose={onClose} />
      <DeleteConversationConfirm flow={flow} />
      <RenameConversationDialog flow={flow} />
    </>
  );
}
